(ns nosco.ui.animations
  (:require
   [hx.react :as hx :refer [defnc]]
   [hx.hooks :refer [useEffect useLayoutEffect useState]]
   ["react" :as React]
   ["@react-spring/web" :refer [useTransition useSpring animated]]
   [nosco.ui.html]
   [nosco.react]))

(defn use-spring [props]
  (if (fn? props)
    (useSpring props)
    (useSpring (clj->js props))))

;; https://github.com/cvpcasada/use-measure/blob/master/src/use-measure.ts
(defn use-measure [ref]
  (let [[bounds set-bounds] (useState {:width 0 :height 0})]
    (useLayoutEffect
     (fn []
       (if-let [node (.-current ref)]
         (let [animation-frame-id (atom nil)
               measure (fn [^js entries]
                         (reset! animation-frame-id (.requestAnimationFrame
                                                     js/window
                                                     (fn []
                                                       (set-bounds {:width (.-inlineSize (first (.-borderBoxSize (first entries))))
                                                                    :height (.-blockSize (first (.-borderBoxSize (first entries))))})))))
               ro (js/ResizeObserver. measure)]
           (.observe ro node)
           (fn []
             (.cancelAnimationFrame js/window @animation-frame-id)
             (.disconnect ro)))
         js/undefined))
     [])
    bounds))

(defn use-previous [value]
  (let [ref (React/useRef)]
    (useEffect (fn [] (set! (.-current ref) value))
               [value])
    (.-current ref)))

(def AnimatedDiv (.-div animated))

(def common-spring-config
  #js {:clamp    true
       :mass     1
       :tension  210
       :friction 20})

(defnc Transition [{:keys [showing children]}]
  (let [transitions (useTransition showing
                                   #js {:config common-spring-config
                                        :from   #js {:opacity 0}
                                        :enter  #js {:opacity 1}
                                        :leave  #js {:opacity 0}})]
    [:<>
     (transitions
      (fn [style item]
        (when item
          (hx/f [AnimatedDiv {:style style
                              :key (str item)}
                 children]))))]))

(defnc Collapse [{:keys [show children scrollable]}]
  (let [previous-show (use-previous show)
        ref           (React/useRef)
        measurements  (use-measure ref)
        anim-props    (use-spring {:from {:height 0}
                                   :to   {:height (if show (:height measurements) 0)}})]
    ;; className instead of class because of annoying hx behaviour that adds
    ;; both class and className, resulting in a React warning
    [AnimatedDiv  {;; helps with performance, asks the browser to create a new layer
                   :style {:will-change "auto"
                           :overflow (if scrollable "auto" "hidden")
                           :height (if (and show (= previous-show show))
                                     "auto"
                                     (.-height anim-props))}}
     [:div {:ref ref}
      children]]))

;; The Collapse component sometimes does not use a transition when expanding
;; because it sets the height to "auto", depending on the values of "previous-show" and "show".
;; If multiple renders happen during the expanding transition, "previous-show" is set
;; to the same value as "show", so the height is set to "auto" and the transition stops,
;; the height is immediately expanded.
;; I don't know why the Collapse component sets the height to "auto" in the first place,
;; thus the addition of Collapse2
(defnc Collapse2 [{:keys [show children scrollable]}]
  (let [ref           (React/useRef)
        measurements  (use-measure ref)
        anim-props    (use-spring {:from {:height 0}
                                   :to   {:height (if show (:height measurements) 0)}})]
    ;; className instead of class because of annoying hx behaviour that adds
    ;; both class and className, resulting in a React warning
    [AnimatedDiv  {;; helps with performance, asks the browser to create a new layer
                   :style {:will-change "auto"
                           :overflow (if scrollable "auto" "hidden")
                           :height (.-height anim-props)}}
     [:div {:ref ref}
      children]]))

(defnc Freeze
  "Freezes the rendered HTML of its contents when `frozen` is truthy.

  This is useful to prevent flashes of empty content when an animated element is
  disappearing after the state has been reset.

  Props:
  - :as - The element to render as the container (default :div)
  - :frozen - Freezes the contents when truthy"
  [{:keys [as frozen children] :or {as :div} :as props} ref]
  (let [[html set-html!] (hx.hooks/useState nil)
        container-ref (React/useRef)]
    (useEffect
     (fn []
       (when (and (not frozen) (.-current container-ref))
         (set-html! (.-current.innerHTML container-ref)))))
    (if frozen
      [nosco.ui.html/UnsafeHTML {:tag as
                                 :ref ref
                                 :html html}]
      [as (-> {:ref (nosco.react/merge-refs container-ref ref)}
              (nosco.react/merge-props (dissoc props :as :frozen)))
       children])))

(nosco.react/forward-ref! Freeze)
